import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import fruitveg from '../assets/images/fruitveg.jpg';
import healthycooking from '../assets/images/healthycooking.jpg';

function Nutrition() {
  return (
    <div className="flex flex-col min-h-screen bg-white">
      <SEO
        title="Nutrition Coaching & Healthy Eating Guidance"
        description="Learn how nutrition complements your training. Practical, sustainable advice on healthy eating, nutritional supplements and building habits that last."
        path="/nutrition"
      />
      <Navbar />

      <main className="flex-grow max-w-7xl mx-auto px-6 lg:px-12 py-6 lg:py-20 text-left">

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-extrabold text-[#162E3B] text-center mb-10">
          Nutrition
        </h1>

        {/* Intro section */}
        <div className="space-y-6 mb-12">
          <p className="text-base sm:text-lg text-gray-800 font-semibold leading-relaxed">
            The power of nutrition is truly underestimated. The impact it can have on our body, on our physical as well as on our mental health, is immense and backed by an infinite amount of scientific research. Yet, are you doing anything to change it for the better?
          </p>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
            To be fair, the modern lifestyle doesn't play in our favor. In a world where everything has to be quick and easy, we have an abundance of fast food around every corner.
          </p>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
          The sad reality is, food loaded with sugar and unhealthy fats have become so normal in today's society, that we almost feel guilty when we eat actual organic food.
          </p>
        </div>

        {/* Self-Reflection Card & Fruit/Veg Image */}
        <div className="flex flex-col lg:flex-row gap-10 items-stretch mb-16">
          <div className="lg:w-3/5 flex flex-col justify-between space-y-6">
            
            {/* Self-Reflection checklist container */}
            <div className="bg-[#162E3B] text-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-base sm:text-lg font-extrabold text-[#FFD82D] mb-6">
                Self-Reflection Checklist
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start text-base sm:text-lg font-bold">
                  <span className="text-[#FFD82D] mr-3 text-xl">?</span>
                  Are fruits and vegetables part of your daily eating?
                </li>
                <li className="flex items-start text-base sm:text-lg font-bold">
                  <span className="text-[#FFD82D] mr-3 text-xl">?</span>
                  Can you actually spend one week without soft drinks or energy drinks?
                </li>
                <li className="flex items-start text-base sm:text-lg font-bold">
                  <span className="text-[#FFD82D] mr-3 text-xl">?</span>
                  Does your meal taste more like salt than the actual food on your plate?
                </li>
                <li className="flex items-start text-base sm:text-lg font-bold">
                  <span className="text-[#FFD82D] mr-3 text-xl">?</span>
                  When was the last time you ate pasta without putting cheese on it?
                </li>
              </ul>
            </div>

            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
            Often times we don't realise how bad we eat. Processed food is dominating our kitchen and we don't even see it. The crazy thing is even if we do have food which is only a little or not processed, we still manage to denaturalize it with the aggressive style of cooking that we have nowadays.
            </p>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
            Unfortunately, a lot of times these eating habits happen to be the source of many diseases and daily issues. From bad skin and lack of motivation to cardiovascular problems and cancer.
            </p>
          </div>

          <div className="lg:w-2/5 w-full">
            <img 
              className="w-full h-full min-h-[300px] object-cover rounded-2xl shadow-lg border border-gray-100" 
              src={fruitveg} 
              alt="Assorted fruits and vegetables" 
            />
          </div>
        </div>

        {/* You Are What You Eat & Quality-Time-Price */}
        <div className="flex flex-col-reverse lg:flex-row gap-10 items-start mb-16">
          <div className="lg:w-1/3 w-full flex flex-col items-center">
            <img 
              className="w-full h-[350px] object-cover rounded-2xl shadow-lg border border-gray-100 mb-4" 
              src={healthycooking} 
              alt="Healthy cooking preparation" 
            />
            <span className="text-lg font-extrabold tracking-widest text-[#162E3B] uppercase">
              You Are What You Eat
            </span>
          </div>

          <div className="lg:w-2/3 space-y-6">
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
            Now what do we have to do to change those habits for the better? Well, we definitely have to understand what is meant by "healthy" and "unhealthy" food because although it can be obvious in some cases, most of the time it's not black or white. We also have to learn how to read the nutrition facts label to be able to know what's actually in it. And we for sure have to have the willpower to make a change in the first place.
            </p>

            {/* Quality, Time, Price callout */}
            <div className="bg-gray-50 border border-gray-150 p-6 rounded-2xl">
              <h4 className="text-lg font-extrabold text-[#162E3B] mb-4">
                The Nutritional Triad
              </h4>
              <p className="text-base sm:text-lg text-gray-600 mb-4">
                When choosing what you eat, you balance three key variables:
              </p>
              <div className="grid grid-cols-3 gap-4 text-center font-bold">
                <div className="p-3 bg-white border border-gray-200 rounded-xl shadow-sm">
                  <span className="text-[#162E3B] text-lg block">Quality</span>
                </div>
                <div className="p-3 bg-white border border-gray-200 rounded-xl shadow-sm">
                  <span className="text-[#162E3B] text-lg block">Time</span>
                </div>
                <div className="p-3 bg-white border border-gray-200 rounded-xl shadow-sm">
                  <span className="text-[#162E3B] text-lg block">Price</span>
                </div>
              </div>
              <p className="text-base sm:text-lg text-gray-600 mt-4">
                In most situations, you sacrifice one of these variables. In the end, it is simply a matter of priorities.
              </p>
            </div>

            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
            The goal is certainly not to be perfect. This would be impossible. Nevertheless, what if you could feel better just by changing what you eat?
            "The fuel of the human body". Nutrition is what makes your body work and is the perfect complement to physical activity. When you give your body what it needs you give yourself endless benefits whether it be recovering better, being more motivated, gaining muscle, losing fat, feeling fitter, strengthening your immune system or boosting your concentration to name just a fraction of them. Forget about any diet you have heard of. Nutrition is a long term way of eating. The goal is to make it enjoyable and sustainable.            </p>
          </div>
        </div>

        {/* Nutritional Supplements Section */}
        <div className="space-y-6 bg-gray-50 border border-gray-150 p-8 rounded-2xl">
          <h3 className="text-xl sm:text-2xl font-extrabold text-[#162E3B]">
            Nutritional Supplements
          </h3>
          
          <p className="text-base text-gray-700 leading-relaxed">
          You probably have heard of this in relation to the fitness industry. Especially in bodybuilding the marketing for supplements is omnipresent. Whey protein, Casein protein, BCAA, Mass gainers, Fat burners, ..., we all have seen them in the gym, in the supermarket, sometimes even in scary XXL packs. I agree that this is a controversial topic. Although popular in the bodybuilding industry and sometimes effective for muscle building purposes only, a lot of these products are probably rather useless for most of us and potentially may even cause more harm than good in the long run. I feel like "supplements" often get a bad name because of this. It can actually be very important to supplement our nutrition, especially nowadays where it becomes more and more difficult to find nutrient dense food. However, I'm talking here about a different type of supplements.
          </p>
          
          <p className="text-base text-gray-700 leading-relaxed">

          The modernisation of the world has certainly many good points and made our lives a lot easier with endless opportunities. Almost everything is possible nowadays. However, if anything is suffering because of it, it's nature, and with it is our nutrition. Industrialization and pollution have made it more difficult for us to get sufficient amounts of essential nutrients through food alone. Today's lifestyle has dramatically decreased the nutrient density so that in order to get the same benefits of eating a "healthy" food, we need to eat so much more of it than before. In other words, eating an apple now doesn't provide near as many beneficial nutrients than if you had eaten the same apple 50 or 100 years ago. Our bodies on the other hand still need the same amount of nutrients to function correctly if not more due to the modern way of life. That's where supplements can become useful and help us get adequate amounts of important nutrients.          </p>

          <p className="text-base text-gray-700 leading-relaxed font-semibold">
          That being said, a supplement will never replace good nutrition! As its name says, it's supposed to supplement an already good nutrition to make it even better if you want the extra benefits.
          </p>

          {/* Highly visible Warning/Disclaimer Alert box */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-5 rounded-r-xl mt-6">
            <div className="flex items-start">
              <span className="text-2xl mr-3">⚠️</span>
              <div>
                <p className="text-amber-900 font-extrabold text-lg mb-1">
                  Important Notice
                </p>
                <p className="text-amber-850 text-base sm:text-lg leading-relaxed">
                  Never start taking any nutritional supplement without doing your own research or discussing it with your medical doctor first.
                </p>
              </div>
            </div>
          </div>
        </div>

      </main>

      <Footer />
    </div>
  );
}

export default Nutrition;
